import { DashboardCard } from "@/core/components/ui/dashboard-card";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { useTranslation } from "react-i18next";
import { useCompanyHighlights } from "@/company/hooks/use-company-highlights";
import { useAuth } from "@/core/components/auth/store/auth";
import { useChatbot } from "@/core/components/floating-chatbot/context/chatbot-context";
import { useActionsTrack } from "@/_old/features/analytics";
import { Skeleton } from "@/core/components/ui/skeleton";
import { AGENT_THEMES } from "@/core/components/floating-chatbot/constants/agents";

export function CollaboratorsCard() {
  const [t] = useTranslation(["company"]);
  const [tCore] = useTranslation(["core"]);
  const {
    userInfo: { user },
  } = useAuth();
  const { openWithMessage } = useChatbot();
  const { onActionClick } = useActionsTrack();

  const { data: companyHighlights, isLoading } = useCompanyHighlights({
    companyId: user.leagues_id[0],
  });

  const registeredMembers = companyHighlights?.registeredMembers || 0;
  const pendingMembers = companyHighlights?.pendingMembers || 0;

  const handleManageMembers = () => {
    onActionClick(
      "DASHBOARD_COLLABORATORS_CARD_MANAGE_MEMBERS_CLICK",
      "DASHBOARD",
      {
        registeredMembers,
        pendingMembers,
      },
    );
    openWithMessage(tCore("chatbot-manage-members-action"));
  };

  const cardTitle = (
    <div className="flex items-center gap-2">
      <span>{t("company-heading-title", "Colaboradores")}</span>
      <Badge className="bg-primary-500 font-medium hover:bg-primary-500 text-black text-xs">
        {tCore("app-menu-badge", "Novo")}
      </Badge>
    </div>
  );

  if (isLoading) {
    return (
      <DashboardCard
        className="bg-card border border-border shadow-sm"
        title={cardTitle}
      >
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="w-16 h-8" />
              <Skeleton className="w-24 h-4" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="w-16 h-8" />
              <Skeleton className="w-24 h-4" />
            </div>
          </div>
          <Skeleton className="w-full h-10" />
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      className="bg-card border border-border shadow-sm py-0"
      title={cardTitle}
      centerContent={true}
    >
      <div className="flex flex-col gap-6 px-4 py-0 justify-center">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col items-center border rounded-lg py-4 px-2">
            <span className="text-2xl font-medium text-gray-900">
              {registeredMembers}
            </span>
            <span className="text-xs text-darkgray-400 text-center lowercase">
              {t("company-tabs-members-label")}
            </span>
          </div>
          <div className="flex flex-col items-center border rounded-lg py-4 px-2">
            <span className="text-2xl font-medium text-gray-900">
              {pendingMembers}
            </span>
            <span className="text-xs text-darkgray-400 text-center lowercase">
              {t("company-highlights-pending-members-title")}
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          className="h-auto min-h-10 w-full whitespace-normal border-aiBlue-500 py-2 text-center font-medium text-aiBlue-500 hover:bg-aiBlue-50"
          onClick={handleManageMembers}
        >
          <img
            src={AGENT_THEMES.operation.iconSrc}
            alt=""
            aria-hidden
            className="mr-2 h-5 w-5 shrink-0 object-contain"
          />
          <span className="min-w-0">{tCore("chatbot-manage-members")}</span>
        </Button>
      </div>
    </DashboardCard>
  );
}
